import { Account, AccountStatus } from 'crono-fe-common/types/account';
import { Prospect, ProspectStatus } from 'crono-fe-common/types/prospect';
import { ExternalValue } from 'crono-fe-common/types/externalValue';
import { ProspectSearchStrategyResponse } from 'crono-fe-common/types/prospectSearchStrategy';
import {
  ExternalProperty,
  ExternalPropertyValueType,
  ProspectOrLead,
  TableType,
} from 'crono-fe-common/types/externalProperty';
import { Tag } from 'crono-fe-common/types/tag';
import { TagInsert } from 'crono-fe-common/types/tagInsert';
import CronoError from 'crono-fe-common/types/error';
import { CallFeedback } from 'crono-fe-common/types/logCall';
import ExternalPropertyFilter from 'crono-fe-common/types/externalPropertyFilter';
import {
  Company,
  CronoGatewayLinkedinGetContactSocialInteractionsAction,
  IsLinkedinCompany,
  LinkedinCompany,
  LinkedinProfileExtendedInfo,
  LinkedinWorkExperience,
} from 'crono-fe-common/types/crono-extension/linkedin';
import {
  Constants,
  statusColors,
  StatusColors,
  statusColorsMap,
} from 'crono-fe-common/constants/constants';
import { ProspectLinkedin } from 'crono-fe-common/types/prospectLinkedin';
import { AccountLinkedin } from 'crono-fe-common/types/accountLinkedin';
import { ProductCreditsDTO } from 'crono-fe-common/types/DTO/SubscriptionPlansDTO';
import {
  differenceInMonths,
  differenceInWeeks,
  differenceInDays,
  differenceInHours,
} from 'date-fns';
import { TemplateVariables } from 'crono-fe-common/types/template';
import { User } from 'crono-fe-common/types/user';
import {
  ITemplateVariables,
  ITemplateVariableUser,
} from 'crono-fe-common/types/templateVariables';
import * as htmlparser2 from 'htmlparser2';
import { convert } from 'html-to-text';
import { Buffer } from 'buffer';
import SubscriptionPlanType from 'crono-fe-common/types/enums/subscriptionPlanType';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import {
  RecipientInfo,
  WorkExperienceShort,
} from 'crono-fe-common/types/DTO/rewriteBaseInput';
import { WithDataForPossibleRewrite } from '../types/withDataForPossibleRewrite';
import { AsyncJobRewriteSequenceInstance } from '../types/crono-extension/background-script';
import { StrategySettings } from '../types/strategySettings';
import { CronoErrorCode } from 'crono-fe-common/types/enums/cronoErrorCode';
import { isTask, Task } from '../types/task';
import { Event } from '../types/event';
import { PreviousActivitiesDTO } from 'crono-fe-common/types/DTO/activitiesDTO';
import IntegrationType from '../types/enums/integrationType';
import { doesCrmSupportLeads } from './crm-utils';
import { ListType } from '../types/list';
import { StrategySearchSort } from 'crono-fe-common/types/DTO/strategySearch';
import { TemplateSearchSort } from 'crono-fe-common/types/DTO/templateSearch';
import { Stage } from '../types/stage';
import { ChildNode } from 'domhandler';

if (typeof window !== 'undefined') {
  window.Buffer = window.Buffer || Buffer;
}

export const getExternalPropertyLabels = (
  property: Pick<ExternalProperty, 'options' | 'labels'>,
) => {
  return property.labels || property.options || [];
};

export const getExternalPropertyOptions = (
  property: Pick<ExternalProperty, 'options'>,
) => {
  return property.options || [];
};
export const getLabel = (
  externalPropertyTags: ExternalProperty[],
  tag: Tag | TagInsert,
) => {
  const externalProperty = externalPropertyTags?.find(
    (externalProperty) => externalProperty.id === tag.externalPropertyId,
  );
  if (!externalProperty || !externalProperty.options) return null;
  return getExternalPropertyLabels(externalProperty)[
    externalProperty.options?.findIndex((property) => property === tag.value)
  ];
};

/**
 * Maps external values to the `currentSituationTags` shape TagsBoxShowAll
 * expects.
 *
 * Pass `tagProperties` whenever the result is used for anything other than
 * feeding TagsBoxShowAll — its length in particular. TagsBoxShowAll looks
 * entries up by externalPropertyId against its own tag query, so extra values
 * are harmless there, but a caller that counts the array needs the values
 * restricted to actual tag properties.
 */
export function getCurrentSituationTags(
  externalValues: ExternalValue[] | undefined | null,
  tagProperties?: ExternalProperty[] | null,
): { externalPropertyId: number; option: string }[] {
  if (!externalValues) {
    return [];
  }
  const tagPropertyIds = tagProperties
    ? new Set(tagProperties.map((externalProperty) => externalProperty.id))
    : null;
  return externalValues
    .filter((ev) => ev.value != null && ev.value !== '')
    .filter(
      (ev) => !tagPropertyIds || tagPropertyIds.has(ev.externalPropertyId),
    )
    .map((ev) => ({
      externalPropertyId: ev.externalPropertyId,
      option: ev.value as string,
    }));
}

//Function to get the status string to display when the type of status is unknown
export function getStringFromUnknownStatus(
  status: AccountStatus | ProspectStatus | null,
) {
  return (
    getStringFromAccountStatus(status as AccountStatus) ||
    getStringFromProspectStatus(status as ProspectStatus)
  );
}

export function getStringFromAccountStatus(status: AccountStatus | null) {
  switch (status) {
    case AccountStatus.INACTIVE:
      return 'Not in target';
    case AccountStatus.NURTURE:
      return 'Nurture';
    case AccountStatus.WORKING:
      return 'Prospecting';
    case AccountStatus.CALL_SCHEDULED:
      return 'Meeting';
    case AccountStatus.OPEN_OPPORTUNITY:
      return 'Opportunity';
    case AccountStatus.CUSTOMER:
      return 'Customer';
    default:
      return '';
  }
}

export function getStringFromProspectStatus(status: ProspectStatus) {
  switch (status) {
    case ProspectStatus.COLD:
      return 'Cold';
    case ProspectStatus.NOT_CONTACTED:
      return 'Not Contacted';
    case ProspectStatus.FIRST_CONTACT:
      return 'First Contact';
    case ProspectStatus.WARM:
      return 'Warm';
    case ProspectStatus.NOT_INTERESTED:
      return 'Not Interested';
    default:
      return '';
  }
}

export function getError(error: CronoError | null) {
  return error?.response?.data?.errors && error.response.data.errors.length > 0
    ? error?.response?.data?.errors?.[0]?.message
    : error?.message;
}

export function printCallFeedback(callFeedback: CallFeedback) {
  switch (callFeedback) {
    case CallFeedback.CONNECTED:
      return 'Connected';
    case CallFeedback.NOT_ANSWERED:
      return 'Not Answered';
    case CallFeedback.GATE_KEEPER:
      return 'Gatekeeper';
    case CallFeedback.VOICEMAIL:
      return 'Voicemail';
    case CallFeedback.WRONG_NUMBER:
      return 'Wrong Number';
    case CallFeedback.BUSY:
      return 'Busy';
    default:
      return '';
  }
}

export function compareVersions(a: string, b: string): number {
  // this is a simple algorithm. If version format gets more complicated, we should switch to 'semver' package
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export const externalPropertiesToTagInsert = (
  input: ExternalPropertyFilter[],
) => {
  const templateTags: TagInsert[] = [];

  input.forEach((ep) => {
    ep?.values?.forEach((option) => {
      templateTags.push({
        externalPropertyId: ep.externalPropertyId,
        value: option,
      });
    });
  });

  return templateTags;
};

export function isCompany(object: unknown): object is Company {
  if (!object) return false;
  return typeof object === 'object' && 'role' in object;
}

function isLinkedinCompany(object: unknown): object is LinkedinCompany {
  if (!object) return false;
  return typeof object === 'object' && 'title' in object;
}

export const extractTitleFromLinkedinCompanyOrCompany = (
  company: Company | LinkedinCompany | null,
): string | null => {
  if (!company) return null;
  const title = isCompany(company)
    ? company.role
    : isLinkedinCompany(company)
      ? company.title
      : null;
  return title ?? null;
};

export const getColorsStatus = (
  statusOrder: number,
  totalNumberOfStatus: number,
) => {
  const numberOfColors = Object.keys(statusColors).length;
  let totalColors: StatusColors[] = [];

  //If the total number of statuss is less than the number of colors we have, we take the colors from the map
  if (totalNumberOfStatus <= numberOfColors) {
    totalColors = statusColorsMap[totalNumberOfStatus];
  } else {
    //In case we have more than the max number of colors we always take 12, erasing the last 2 colors and all the excessive ones at the end
    let currentTotal = totalNumberOfStatus;
    while (currentTotal > 0) {
      totalColors = totalColors.concat(statusColorsMap[numberOfColors]);
      currentTotal -= numberOfColors;
    }
  }

  return totalColors[statusOrder] ?? statusColorsMap[numberOfColors][0];
};

export function checkIfValidPhone(phone: string): boolean {
  const regex = Constants.phoneRegex;
  return regex.test(phone);
}

export function checkIfValidEmail(email: string): boolean {
  const regex = Constants.emailRegex;
  return regex.test(email);
}

export function checkIfValidUrl(url: string): boolean {
  const regex = Constants.urlRegex;
  return regex.test(url);
}

export function transformToValidUrl(
  url: string | null | undefined,
): string | null {
  if (!url) return null;
  if (checkIfValidUrl(url)) return url;

  const newUrl = `http://${url}`;
  if (checkIfValidUrl(newUrl)) return newUrl;

  return null;
}

export const withProtocol = (url: string): string =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

export const numberToCardinality = (
  n: number | null | undefined,
): string | null => {
  if (n == null || n <= 0) return null;
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
};

interface DateObject {
  month?: number;
  year: number;
}

export const calculateDuration = (
  startDate: DateObject,
  endDate?: DateObject,
): { years: number; months: number } => {
  const currentDate = new Date();
  const end = endDate
    ? new Date(endDate.year, (endDate.month ?? 1) - 1)
    : currentDate;
  const start = new Date(startDate.year, (startDate.month ?? 1) - 1);

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  return { years, months };
};

/**
 * LinkedIn only exposes month precision on a position's start date, so the
 * result is the last day the given month (or year, when the month is unknown)
 * can stand for. The consumer is the API's freshness cutoff, which compares
 * whole days and already treats an unknown date as recent: rounding to the end
 * of the known precision keeps that bias, where rounding to the start would
 * discard a change that actually falls inside the window.
 */
export const timePeriodStartToIsoDate = (
  startDate: DateObject | undefined,
): string | undefined => {
  if (!startDate) {
    return undefined;
  }

  // Day 0 of a month is the last day of the one before it, so a 1-based month
  // passed as Date.UTC's 0-based index lands on the end of that same month, and
  // 12 lands on 31 December.
  const { month, year } = startDate;
  const monthAfterPeriod =
    month != null && month >= 1 && month <= 12 ? month : 12;

  return new Date(Date.UTC(year, monthAfterPeriod, 0)).toISOString();
};

interface TimePeriod {
  timePeriod?: {
    startDate: DateObject;
    endDate?: DateObject;
  };
}

export const sortByTimePeriod = (a: TimePeriod, b: TimePeriod) => {
  if (!a.timePeriod || !b.timePeriod) {
    return 0;
  }
  if (a.timePeriod.startDate.year < b.timePeriod.startDate.year) {
    return 1;
  } else if (a.timePeriod.startDate.year > b.timePeriod.startDate.year) {
    return -1;
  } else {
    if (
      (a.timePeriod.startDate.month ?? 1) < (b.timePeriod.startDate.month ?? 1)
    ) {
      return 1;
    } else if (
      (a.timePeriod.startDate.month ?? 1) > (b.timePeriod.startDate.month ?? 1)
    ) {
      return -1;
    } else {
      return 0;
    }
  }
};

export const roundDateToMinutes = (date: Date) => {
  const roundedValue = new Date(date);
  roundedValue.setHours(date.getHours());
  roundedValue.setMinutes(date.getMinutes());
  roundedValue.setSeconds(0);
  roundedValue.setMilliseconds(0);
  return roundedValue;
};

export const getCompanyIdFromUrn = (urn: string) => {
  // get all digits from the end of the string
  const match = urn.match(/(\d+)$/);
  return match ? match[0] : null;
};

export function htmlToText(html: string, stripRedundantBreaks?: boolean) {
  //remove code brakes and tabs
  html = html.replace(/\n/g, '');
  html = html.replace(/\t/g, '');

  if (stripRedundantBreaks) {
    // Remove <br> tags that are the sole content of a <p> tag
    // (e.g. <p style="margin: 0px;"><br></p>)
    // The <p> itself already produces a line break, so the <br> inside is redundant
    html = html.replace(/<p([^>]*)><br\s*\/?><\/p>/gi, '<p$1></p>');
  }

  const parsedContent = convert(html, {
    selectors: [
      {
        selector: 'p',
        options: {
          leadingLineBreaks: 1,
          trailingLineBreaks: 1,
        },
      },
      {
        selector: 'br',
        format: 'lineBreak',
      },
    ],
    wordwrap: null,
  });

  return parsedContent;
}

export function htmlToLinkedinText(html: string) {
  //remove code brakes and tabs
  html = html.replace(/\n/g, '');
  html = html.replace(/\t/g, '');

  const parsedContent = convert(html, {
    selectors: [
      {
        selector: 'p',
        options: {
          leadingLineBreaks: 1,
          trailingLineBreaks: 1,
        },
      },
      {
        selector: 'br',
        format: 'inline', // we have <p></br></p> in our editor, so <p> will create one line and <br> should not create another one
      },
    ],
    wordwrap: null,
  });

  return parsedContent;
}

export function convertPeopleStringToNumber(
  peopleString: number | string | null,
): number | null {
  if (!peopleString) return null;
  if (typeof peopleString === 'number') return peopleString;
  peopleString = peopleString.toLowerCase();
  // Removing commas and plus sign
  peopleString = peopleString.replace(/,/g, '.').replace(/\+/g, '').trim();

  // Handling different suffixes
  if (peopleString.toLowerCase().includes('k')) {
    return parseFloat(peopleString.replace(/k/i, '')) * 1000;
  } else if (peopleString.toLowerCase().includes('m')) {
    return parseFloat(peopleString.replace(/m/i, '')) * 1000000;
  } else {
    return parseFloat(peopleString);
  }
}

//Prospect exposes ownership as `owned`, ProspectLinkedin as `owner`
export const isProspectOwned = (
  prospect: Prospect | ProspectLinkedin,
): boolean =>
  'owned' in prospect ? (prospect.owned ?? false) : prospect.owner;

export const mapProspectToProspectLinkedinAndCorrect = (
  prospect: Prospect | null,
): ProspectLinkedin | null => {
  if (
    !prospect ||
    !prospect.objectId ||
    !prospect.accountId ||
    !prospect.account?.objectId
  )
    return null;
  const newProspect: ProspectLinkedin = {
    ...prospect,
    name: prospect.name ?? null,
    correct: true,
    owner: prospect?.owned ?? false,
    firstName: prospect.firstName ?? null,
    lastName: prospect.lastName ?? null,
    linkedin: prospect.linkedin ?? null,
    email: prospect.email ?? null,
    phone: prospect.phone ?? null,
    title: prospect.title ?? '',
    account: {
      ...prospect.account,
      owner: prospect.account?.owned ?? false,
      canModify: prospect.account?.owned ?? false,
    },
  };
  return newProspect;
};

export const mapAccountToAccountLinkedinAndCorrect = (
  account: Account | null,
): AccountLinkedin | null => {
  if (!account || !account.objectId) return null;
  const newAccount: AccountLinkedin = {
    ...account,
    owner: account.owned ?? false,
    canModify: account.owned ?? false,
    correct: true,
  };
  return newAccount;
};

export const ensureArray = <T>(anyOrArray: T | T[]) => {
  return Array.isArray(anyOrArray) ? anyOrArray : [anyOrArray];
};

export const getCreditProductLabels = (credits: ProductCreditsDTO[]) => {
  return credits.map((el) => getCreditProductLabel(el));
};

export const getCreditProductLabel = (el: ProductCreditsDTO) => {
  const credits = el.credits ?? 0;
  const price = el.price?.amount ?? 0;
  const currency = el.price?.currency?.toLowerCase() === 'eur' ? '€' : '$';
  return `${credits} - ${price}${currency}`;
};

export const getOptionsMultiselect = (option: string | null) => {
  if (!option) return [];
  return option.split(Constants.multiselectOptionsDivider);
};

export const setOptionsMultiselect = (options: string[] | null) => {
  if (!options || options.length === 0) return null;
  return options.join(Constants.multiselectOptionsDivider);
};

export const getNewOptionsWhenSelecting = ({
  externalPropertyId,
  valueType,
  option,
  currentExternalValues,
}: {
  externalPropertyId: number;
  valueType: ExternalPropertyValueType;
  option: string | null;
  currentExternalValues: (ExternalValue | TagInsert)[] | null;
}) => {
  if (option === null) {
    return null;
  }
  const old = currentExternalValues?.find(
    (tag) => tag.externalPropertyId === externalPropertyId,
  );
  let newOptions: string | string[] | null | undefined = null;
  let finalOption: string | null = null;
  if (valueType === 'MultiSelect') {
    newOptions = getOptionsMultiselect(old?.value ?? null);
    if (newOptions.includes(option)) {
      newOptions = newOptions.filter((el) => el !== option);
    } else {
      newOptions.push(option);
    }
    if (newOptions.length > 0) {
      finalOption = setOptionsMultiselect(newOptions);
    }
  } else {
    newOptions = option;

    if (old && old.value === newOptions) {
      newOptions = null;
    }
    finalOption = newOptions ?? null;
  }
  return finalOption;
};

export const getNewOptionsWhenSelectingWithStrings = ({
  valueType,
  option,
  currentValue,
}: {
  valueType: ExternalPropertyValueType;
  option: string | null;
  currentValue: string | null;
}) => {
  if (option === null) {
    return null;
  }
  let newOptions: string | string[] | null | undefined = null;
  let finalOption: string | null = null;
  if (valueType === 'MultiSelect') {
    newOptions = getOptionsMultiselect(currentValue ?? null);

    if (newOptions.includes(option)) {
      newOptions = newOptions.filter((el) => el !== option);
    } else {
      newOptions.push(option);
    }

    if (newOptions.length > 0) {
      finalOption = setOptionsMultiselect(newOptions);
    }
  } else {
    newOptions = option;

    if (currentValue === newOptions) {
      newOptions = null;
    }
    finalOption = newOptions ?? null;
  }
  return finalOption;
};

export const toFixedOnlyIfNeeded = (value: number) => {
  return Math.round(value * 100) / 100;
};

export const getDateDiff = (date: Date) => {
  const now = new Date();
  let diff = differenceInMonths(now, date);
  let time = 'm';

  if (diff === 0) {
    diff = differenceInWeeks(now, date);
    time = 'w';
    if (diff === 0) {
      diff = differenceInDays(now, date);
      time = 'd';
      if (diff === 0) {
        diff = differenceInHours(now, date);
        time = 'h';
      }
    }
  }

  return diff === 0 ? 'Just now' : `${diff}${time} ago`;
};

export function checkIfValidWebsite(url: string): boolean {
  const regex = Constants.websiteRegex;
  return regex.test(url);
}

export function extractDomainFromWebsite(url: string): string {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = 'https://' + url;
  }
  try {
    // test url regex
    if (checkIfValidWebsite(url)) {
      const u = new URL(url);
      return u.host.replace('www.', '');
    } else {
      return '';
    }
  } catch (e) {
    return '';
  }
}

export const getImageFromUrl = (url: string | null, size: number | null) => {
  if (!url) return Constants.defaultImageUrl;
  const domain = extractDomainFromWebsite(url);
  if (!domain) return Constants.defaultImageUrl;

  if (!size) size = 64;

  // we use url instead of domain because it retrieves icon in more cases
  return `https://www.google.com/s2/favicons?sz=${size}&domain_url=${url}`;
};

export const stringAvatarAccount = (name: string) =>
  name.toUpperCase().substring(0, 2);

export const fullName = (first?: string | null, last?: string | null) => {
  const firstname = first && first !== 'null' ? first : '';
  const lastname = last && last !== 'null' ? last : '';
  if (firstname === '' && lastname === '') return '';
  return `${firstname} ${lastname}`;
};

export const getProspectName = (
  prospect?: {
    name?: string | null;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null,
) => {
  if (!prospect) return '';
  let name = fullName(prospect.firstName, prospect.lastName);
  if (name === '') {
    if (prospect.name && prospect.name.trim() !== '') {
      name = prospect.name;
    } else {
      name = prospect.email ?? '';
    }
  }
  if (name.trim() === '') {
    name = 'No name';
  }
  return name;
};

export const formatHtmlContent = (content: string): string => {
  //===============================
  const document = htmlparser2.parseDocument(content ?? '');
  // Traverse and modify the DOM
  const modifyElement = (element: ChildNode) => {
    if ('type' in element && element.type === 'tag') {
      if ('name' in element && element.name === 'p') {
        let style = ('attribs' in element && element.attribs?.style) || '';
        if (!style.includes('margin')) {
          style += ' margin: 0px;';
        }
        if (element.attribs) element.attribs.style = style;
      }

      if (['ul', 'ol'].includes(element.name)) {
        let style = element.attribs?.style || '';
        if (!style.includes('margin')) {
          style += ' margin: 0;';
        }
        if (!style.includes('padding-left')) {
          style += ' padding-left: 1.5em;';
        }
        if (element.attribs) element.attribs.style = style;
      }
    }

    // Recursively modify child elements
    if ('children' in element && element.children) {
      element.children.forEach(modifyElement);
    }
  };

  document.children.forEach(modifyElement);
  const finalContent = htmlparser2.DomUtils.getOuterHTML(document);

  //Fix the missing variables brackets
  const formattedContent: string = formatMissingVariableBrackets(finalContent);
  // Convert the DOM back to HTML
  return formattedContent;
};

export const TEMPLATE_VARIABLE_MISSING = '{{';

export function getMissingOtherTemplateVariables(content: string): string[] {
  const missingVariables: string[] = [];
  let index = -1;
  let occurrence = -1;
  occurrence = content.indexOf(TEMPLATE_VARIABLE_MISSING, index);
  while (occurrence !== -1) {
    //Find the end of the variable
    const end = content.indexOf('}}', occurrence);

    //If we found no end let's go on to the next one, since this is incomplete
    if (end === -1) {
      index = occurrence + 2;
      occurrence = content.indexOf(TEMPLATE_VARIABLE_MISSING, index);
      continue;
    }
    const possibleTemplateVariable = content.substring(occurrence + 2, end + 2);

    //Check if the variable is an actual one or if may be the following situation {{EP_{{EP_1_name}}
    //If we find further beginning of variables, we skip this one
    if (possibleTemplateVariable.includes(TEMPLATE_VARIABLE_MISSING)) {
      index = occurrence + 2;
      occurrence = content.indexOf(TEMPLATE_VARIABLE_MISSING, index);
      continue;
    }

    //Add the variable to the list of missing variables if not already present
    const variable = content.substring(occurrence, end + 2);
    if (!missingVariables.includes(variable)) {
      missingVariables.push(variable);
    }
    index = end + 2;
    occurrence = content.indexOf(TEMPLATE_VARIABLE_MISSING, index);
  }

  return missingVariables;
}

export const getMissingTemplateVariables = (
  message: string,
  subject?: string,
): string[] => {
  let missingVariables: string[] = [];

  Object.values(TemplateVariables).map((variable) => {
    if (message?.includes(variable) || subject?.includes(variable)) {
      missingVariables.push(variable);
    }
  });
  missingVariables = missingVariables.concat(
    getMissingOtherTemplateVariables(message),
  );
  return missingVariables;
};

export function replaceTemplateVariable(
  content: string,
  variable: TemplateVariables | string,
  toReplace: string | null,
): string {
  if (!toReplace) return content;
  return content.replace(new RegExp(variable, 'g'), toReplace);
}

export const formatMissingVariableBrackets = (content: string) => {
  // Regular expression to capture segments enclosed by any number of braces
  const regex = /{?{?[^{}]+}?}?/g;

  return content.replace(regex, (match) => {
    // Remove any extra whitespace
    const trimmedMatch = match.trim();

    // Only modify if the segment contains variable-like content
    if (trimmedMatch.includes('{') || trimmedMatch.includes('}')) {
      let corrected = trimmedMatch;
      // Add leading '{{' if missing
      if (!trimmedMatch.startsWith('{{')) {
        corrected = '{{' + corrected.replace(/^({?)/, '');
      }

      // Add trailing '}}' if missing
      if (!trimmedMatch.endsWith('}}')) {
        corrected = corrected.replace(/}?$/, '') + '}}';
      }

      return corrected;
    }

    // Return unchanged if it's just regular content
    return match;
  });
};

export function getCorrectFormatOfTemplateVariable(
  templateVariable: string | null,
  replaceNewLinesWithBr: boolean,
) {
  if (replaceNewLinesWithBr) {
    return templateVariable?.replace(/\\n|\n/gm, '<br>') ?? '';
  }
  return templateVariable;
}

/**
 * Label for the selected option of a List property, or null when the value is
 * not among the property's options — the property list is fetched separately
 * from the values and cached, so the two can legitimately disagree.
 */
function getListOptionLabel(
  externalProperty: ExternalProperty,
  value: string,
): string | null {
  const options = externalProperty.options ?? externalProperty.labels ?? [];
  const optionIndex = options.findIndex((option) => option === value);
  if (optionIndex < 0) return null;
  return (
    (externalProperty.labels ?? externalProperty.options ?? []).at(
      optionIndex,
    ) ?? null
  );
}

export function replaceAllTemplateVariables(
  content: string,
  prospect:
    | Prospect
    | {
        name?: string | null;
        title?: string | null;
        location?: string | null;
        user?: string | null;
      }
    | null,
  account:
    | Account
    | AccountLinkedin
    | Company
    | LinkedinCompany
    | Omit<Account, 'prospects'>
    | null,
  /**
   * Whoever the content is written as — the logged-in user for their own
   * sequences, the sequence owner when a manager personalizes one of theirs.
   */
  user: User | ITemplateVariableUser | null,
  templateVariables: ITemplateVariables | null,
  /**
   * Properties for every table type whose values may appear in the content
   * (Account + Prospect/Lead). Custom-field variables cannot be resolved
   * without it, since values only carry `externalPropertyId`.
   */
  externalProperties: ExternalProperty[] | null,
  substituteNewLinesWithBr = false,
): string {
  let formattedContent: string = formatMissingVariableBrackets(content);

  if (templateVariables) {
    templateVariables.templateSnippets.forEach((_, i) => {
      formattedContent = replaceTemplateVariable(
        formattedContent,
        templateVariables.templateSnippets[i].templateVariableName,
        getCorrectFormatOfTemplateVariable(
          templateVariables.templateSnippets[i].content ?? '',
          substituteNewLinesWithBr,
        ),
      );
    });
    formattedContent = replaceTemplateVariable(
      formattedContent,
      templateVariables.productNameTemplateVariable,
      getCorrectFormatOfTemplateVariable(
        templateVariables.productName,
        substituteNewLinesWithBr,
      ),
    );

    formattedContent = replaceTemplateVariable(
      formattedContent,
      templateVariables.productDescriptionTemplateVariable,
      getCorrectFormatOfTemplateVariable(
        templateVariables.productDescription,
        substituteNewLinesWithBr,
      ),
    );

    templateVariables.productReferences.forEach((_, i) => {
      formattedContent = replaceTemplateVariable(
        formattedContent,
        templateVariables.productReferencesTemplateVariables[i],
        getCorrectFormatOfTemplateVariable(
          templateVariables.productReferences[i],
          substituteNewLinesWithBr,
        ),
      );
    });

    templateVariables.productBenefits.forEach((_, i) => {
      formattedContent = replaceTemplateVariable(
        formattedContent,
        templateVariables.productBenefitsTemplateVariables[i],
        getCorrectFormatOfTemplateVariable(
          templateVariables.productBenefits[i],
          substituteNewLinesWithBr,
        ),
      );
    });

    templateVariables.productIndustries.forEach((_, i) => {
      formattedContent = replaceTemplateVariable(
        formattedContent,
        templateVariables.productIndustriesTemplateVariables[i],
        getCorrectFormatOfTemplateVariable(
          templateVariables.productIndustries[i],
          substituteNewLinesWithBr,
        ),
      );
    });
  }

  let prospectFirstName: string | null = null;
  let prospectLastName: string | null = null;

  if (prospect) {
    if ('firstName' in prospect && prospect.firstName) {
      prospectFirstName = prospect.firstName;
    } else if (prospect.name) {
      prospectFirstName = prospect.name.split(' ')[0];
    }

    if ('lastName' in prospect && prospect.lastName) {
      prospectLastName = prospect.lastName;
    } else if (prospect.name) {
      prospectLastName = prospect.name
        .replace(prospect.name.split(' ')[0], '')
        .trim();
    }
  }

  formattedContent = replaceTemplateVariable(
    formattedContent,
    TemplateVariables.PROSPECT_NAME,
    prospectFirstName,
  );
  formattedContent = replaceTemplateVariable(
    formattedContent,
    TemplateVariables.PROSPECT_SURNAME,
    prospectLastName,
  );
  formattedContent = replaceTemplateVariable(
    formattedContent,
    TemplateVariables.PROSPECT_TITLE,
    prospect?.title ?? null,
  );
  formattedContent = replaceTemplateVariable(
    formattedContent,
    TemplateVariables.PROSPECT_LOCATION,
    prospect?.location ?? null,
  );
  formattedContent = replaceTemplateVariable(
    formattedContent,
    TemplateVariables.COMPANY_NAME,
    IsLinkedinCompany(account) ? account.title : (account?.name ?? null),
  );
  formattedContent = replaceTemplateVariable(
    formattedContent,
    TemplateVariables.USER_NAME,
    user?.firstName ?? null,
  );
  formattedContent = replaceTemplateVariable(
    formattedContent,
    TemplateVariables.USER_SURNAME,
    user?.lastName ?? null,
  );
  formattedContent = replaceTemplateVariable(
    formattedContent,
    TemplateVariables.USER_COMPANY_NAME,
    user?.company?.name ?? null,
  );
  formattedContent = replaceTemplateVariable(
    formattedContent,
    TemplateVariables.PROSPECT_OWNER,
    prospect?.user ?? null,
  );
  formattedContent = replaceTemplateVariable(
    formattedContent,
    TemplateVariables.COMPANY_OWNER,
    account && 'user' in account && account?.user ? account.user : null,
  );

  const externalValueUsed = [
    ...(!IsLinkedinCompany(account) && !isCompany(account)
      ? (account?.externalValues ?? [])
      : []),
    ...(prospect && 'externalValues' in prospect
      ? (prospect?.externalValues ?? [])
      : []),
  ];

  const externalPropertyById = new Map(
    (externalProperties ?? []).map((externalProperty) => [
      externalProperty.id,
      externalProperty,
    ]),
  );

  externalValueUsed.forEach((externalValue) => {
    if (!externalValue || !externalValue.value) return;
    const externalProperty = externalPropertyById.get(
      externalValue.externalPropertyId,
    );
    if (!externalProperty?.templateVariable) return;
    formattedContent = replaceTemplateVariable(
      formattedContent,
      externalProperty.templateVariable,
      //If the externalProperty is a list, we need to get the label of the selected option
      getCorrectFormatOfTemplateVariable(
        externalProperty.valueType === 'List'
          ? getListOptionLabel(externalProperty, externalValue.value)
          : externalValue.value,
        substituteNewLinesWithBr,
      ),
    );
  });

  return formattedContent;
}

export const adjustDateToTimezone = (
  originalDate: Date,
  timezoneOffset: string,
): string => {
  const offsetMatch = timezoneOffset.match(/([+-])(\d{2}):(\d{2})/);
  if (offsetMatch) {
    const sign = offsetMatch[1] === '+' ? 1 : -1;
    const hours = parseInt(offsetMatch[2], 10);
    const minutes = parseInt(offsetMatch[3], 10);
    const totalOffsetMinutes = sign * (hours * 60 + minutes);
    // Shift from UTC to the target offset
    const offsetDate = new Date(
      originalDate.getTime() + totalOffsetMinutes * 60 * 1000,
    );
    const iso = offsetDate.toISOString(); // e.g. "2024-01-15T10:30:00.000Z"
    // Replace the trailing "Z" and milliseconds with the offset string
    return iso.replace(/\.\d{3}Z$/, timezoneOffset);
  }
  return new Date(originalDate).toISOString();
};

// Example usage:
const originalDate = new Date(); // Current date
const timezoneOffset = '+05:30'; // Adjust to a specific timezone
const adjustedDate = adjustDateToTimezone(originalDate, timezoneOffset);

export const getCronoChromeExtensionId = () => {
  if (process.env.VITE_CRONO_CHROME_EXTENSION_ID) {
    return process.env.VITE_CRONO_CHROME_EXTENSION_ID;
  } else {
    return process.env.VITE_APP_CRONO_CHROME_EXTENSION_ID;
    //  ?? import.meta.env.VITE_APP_CRONO_CHROME_EXTENSION_ID
  }
};

export const getCronoChromeExtensionUrl = () => {
  if (process.env.VITE_CRONO_CHROME_EXTENSION_URL) {
    return process.env.VITE_CRONO_CHROME_EXTENSION_URL;
  } else {
    return process.env.VITE_APP_CRONO_CHROME_EXTENSION_URL;
  }
};

export const capitalize = (value: string) =>
  `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

export const removeSpecialCharacters = (text: string) => {
  return text.trim().replace(/[^\p{L}\p{N}\s'\-_~]/gu, '');
};

export const showUpgradeBadgesUtil = (user: User | null) => {
  if (!user) return false;
  if (
    user.subscriptionType &&
    user.subscriptionType === SubscriptionPlanType.STARTER
  ) {
    return true;
  }
  return false;
};

export const parseFirstLastName = (fullName: string) => {
  let [firstName, ...lastName] = removeSpecialCharacters(fullName)
    .trim()
    .split(' ');

  //Clean titles like Dr. or Dr from the first name
  if (firstName.toLowerCase() === 'dr') {
    [firstName, ...lastName] = lastName;
  }

  //Remove everything after a Comma in the last name
  let lastNameJoined = lastName.join(' ');
  if (lastNameJoined.includes(',')) {
    lastNameJoined = lastNameJoined.split(',')[0];
  }
  //We want to get the first lastName, and starting from the second we can filter out the titles
  const [firstLastName, ...otherLastNames] = lastNameJoined.split(' ');

  //Remove icons that have the for of :iconName: and other special characters
  const clearFirstName = firstName.trim();

  const clearOtherLastName = removeSpecialCharacters(
    ['', ...otherLastNames].join(' '),
  );
  //Remove all texts  MBA or PHD case insensitive and only if something else is before it (and if they are not part of a longer word)
  //I add an empty string at the beginning so that if the first in otherLastNames is one of the filtered words, it is removed
  const otherLastNameFiltered = clearOtherLastName
    .replace(
      / MBA| PHD| EMBA| PMP| FRM| AIGP| CIPT| CIPPE| CIPPEUS| MICA| FIP| CIPM| CDPOBR| ECPC-B| ECPCB| EDPB| IAPP| LIB| CDPO/gi,
      '',
    )
    .trim();
  let clearLastName = removeSpecialCharacters(
    `${firstLastName} ${otherLastNameFiltered}`,
  );

  //If the clearLastName has a non alphanumerical character at the end, remove it and trim again
  if (
    clearLastName.length > 0 &&
    !clearLastName[clearLastName.length - 1].match(/[\p{L}0-9.]+/gu)
  ) {
    clearLastName = clearLastName.slice(0, -1).trim();
  }

  return {
    firstName: clearFirstName,
    lastName: clearLastName,
    name: `${clearFirstName} ${clearLastName}`,
  };
};

export const getRecipientInfo = (
  recipientInsights: LinkedinProfileExtendedInfo,
  recipientProfileDescription: string | null,
  recipientSocialInteraction:
    | CronoGatewayLinkedinGetContactSocialInteractionsAction['result']
    | null,
): RecipientInfo => {
  return {
    firstName: recipientInsights.firstName,
    lastName: recipientInsights.lastName,
    overallExperienceTime: recipientInsights.overallExperienceTime,
    location: recipientInsights.location,
    linkedinProfileDescription:
      recipientProfileDescription ?? recipientInsights.description,
    linkedinHashTags: recipientSocialInteraction?.allHashtags
      ? Object.keys(recipientSocialInteraction?.allHashtags)
      : undefined,
    workExperience: recipientInsights?.workExperience?.map((w) => ({
      position: w.position,
      companyName: w.company.name,
      timePeriod: w.timePeriod,
      description: w.description,
    })),
  };
};

export const getWorkExperienceShort = (
  linkedinWorkExperiences: LinkedinWorkExperience[] | null,
): WorkExperienceShort[] => {
  return (
    linkedinWorkExperiences?.map((w) => ({
      position: w.position,
      companyName: w.company.name,
      timePeriod: w.timePeriod,
      description: w.description,
    })) ?? []
  );
};

type EnqueueRewriteJobFunction = (
  params: Omit<AsyncJobRewriteSequenceInstance, 'id' | 'state'>[],
) => Promise<unknown>;

type SourceType = AsyncJobRewriteSequenceInstance['source'];

export const enqueueMultipleRewriteSequenceInstancesIfNeeded = async (
  requestAndRewriteDataArray: WithDataForPossibleRewrite<unknown>[],
  enqueueRewriteJob: EnqueueRewriteJobFunction,
  source: SourceType = 'crono-fe',
) => {
  const batch = requestAndRewriteDataArray
    .filter((requestAndRewriteData) =>
      isRewriteOnInsertEnabled(requestAndRewriteData.strategySettings),
    )
    .map((requestAndRewriteData) => {
      const { data: dataOfActualRequest, ...dataForRewrite } =
        requestAndRewriteData;
      return {
        strategySettings: dataForRewrite.strategySettings,
        recipient: dataForRewrite.recipient,
        source,
      };
    });

  if (batch.length > 0) {
    await enqueueRewriteJob(batch);
  }
};

export const isRewriteOnInsertEnabled = (
  strategySettings?: StrategySettings | null,
) => {
  return !!(
    strategySettings &&
    strategySettings.selectedInsightsForRewrite &&
    strategySettings.selectedInsightsForRewrite.length > 0
  );
};

export const enqueueRewriteSequenceInstanceIfNeeded = async (
  requestAndRewriteData: WithDataForPossibleRewrite<unknown>,
  enqueueRewriteJob: EnqueueRewriteJobFunction,
  source: SourceType = 'crono-fe',
) => {
  const { data: dataOfActualRequest, ...dataForRewrite } =
    requestAndRewriteData;

  if (isRewriteOnInsertEnabled(dataForRewrite.strategySettings)) {
    const batch = [
      {
        strategySettings: dataForRewrite.strategySettings,
        recipient: dataForRewrite.recipient,
        source,
      },
    ];
    await enqueueRewriteJob(batch);
  }
};

export const showConnectionLevel = (value: string) => {
  if (value && !isNaN(Number(value))) {
    switch (value) {
      case '1':
        return `${value}st`;
      case '2':
        return `${value}nd`;
      case '3':
        return `${value}rd+`;
      default:
        return value;
    }
  }
  return value;
};

export const getMaxNumberOfMailboxAllowed = (
  subscriptionType: SubscriptionPlanType,
) => {
  switch (subscriptionType) {
    case SubscriptionPlanType.FREE_TRIAL:
      return 1;
    case SubscriptionPlanType.STARTER:
      return 0;
    case SubscriptionPlanType.PRO:
      return 3;
    case SubscriptionPlanType.ULTRA:
      return 10;
    default:
      return 0;
  }
};

export function getEnrichError(error: CronoErrorCode, tableType: TableType) {
  switch (error) {
    case CronoErrorCode.GenerateAiVariableEntityNotFound:
      return 'Error while obtaining information regarding the current entity';
    case CronoErrorCode.GenerateAiVariableVariableNotFound:
      return `Missing variables. Check if variables used in the prompt are present for the ${
        tableType === 'Account'
          ? 'company'
          : tableType === 'Prospect'
            ? 'contact'
            : 'lead'
      }`;
    case CronoErrorCode.GenerateAiVariablePromptError:
      return 'Error while retrieving or generating the prompt for the AI model';
    case CronoErrorCode.GenerateAiVariableVariableMissingError:
      return 'Some of the variables are missing. Check if they are still present in the system';
    case CronoErrorCode.GenerateAiVariableVariableEmptyPromptError:
      return 'Some of the variables are empty. Populate it to proceed';
    case CronoErrorCode.GenerateAiVariableInternalError:
      return 'Internal error while generating the value';
    case CronoErrorCode.AssistantRequestsFinished:
      return 'Not enough credit to complete the request';
    default:
      return 'An error occurred while generating the value';
  }
}

export function getOptedOutUserName(prospect: Prospect): string | null {
  if (prospect.optOutUserId) {
    return prospect.optOutUser ?? null;
  }
  return prospect.name ?? null;
}
export const convertSecondsToMilliseconds = (seconds: number) => seconds * 1000;

export const getActivityDate = (activity: Task | Event) => {
  if (isTask(activity)) {
    return activity.activityDate;
  } else {
    return activity.activityDateTime;
  }
};

export const sortActivities = (
  activities: PreviousActivitiesDTO[] | undefined,
) => {
  const tasks = activities
    ?.filter((activity) => activity.event === null)
    .map((activity) => activity.task);
  const events = activities
    ?.filter((activity) => activity.task === null)
    .map((activity) => activity.event);

  const taskAndEvents = [...(tasks || []), ...(events || [])];
  return taskAndEvents.sort((a, b) => {
    return (
      new Date(getActivityDate(b)).getTime() -
      new Date(getActivityDate(a)).getTime()
    );
  });
};

export const getProspectTableType = (
  integrationType?: IntegrationType | null,
  createAsLead?: boolean,
): ProspectOrLead => {
  if (doesCrmSupportLeads(integrationType)) {
    return createAsLead ? 'Lead' : 'Prospect';
  }

  return 'Prospect';
};

export const getProspectListType = (
  integrationType?: IntegrationType | null,
  createAsLead?: boolean,
): ListType.Prospect | ListType.Lead => {
  if (doesCrmSupportLeads(integrationType)) {
    return createAsLead ? ListType.Lead : ListType.Prospect;
  }

  return ListType.Prospect;
};

export const renderStrategySortOptions = (
  sortName: StrategySearchSort,
): string => {
  switch (sortName) {
    case 'UsageDesc':
      return 'Most used';
    case 'CreatedDesc':
      return 'Creation date (newest first)';
    case 'Created':
      return 'Creation date (oldest first)';
    case 'Name':
      return 'Name (A-Z)';
    case 'NameDesc':
      return 'Name (Z-A)';
    case 'Replied':
      return 'Replied';
    default:
      return '';
  }
};

type TemplateSortOption = { label: string; value: TemplateSearchSort };

const baseTemplateSortOptions: TemplateSortOption[] = [
  { label: 'Most used by me', value: 'UsageByMeDesc' },
  { label: 'Least used by me', value: 'UsageByMe' },
  { label: 'Recently used', value: 'RecentlyUsed' },
  { label: 'Most used by team', value: 'UsageBySubscriptionDesc' },
  { label: 'Least used by team', value: 'UsageBySubscription' },
  { label: 'Creation date (newest first)', value: 'CreatedDesc' },
  { label: 'Creation date (oldest first)', value: 'CreatedAsc' },
  { label: 'Name (A-Z)', value: 'NameAsc' },
  { label: 'Name (Z-A)', value: 'NameDesc' },
];

export const templatesSortingOptions: Record<
  TemplateType,
  TemplateSortOption[]
> = {
  [TemplateType.EMAIL]: [
    ...baseTemplateSortOptions,
    { label: 'Most opened', value: 'Opened' },
    { label: 'Least opened', value: 'OpenedAsc' },
    { label: 'Most replied', value: 'Replied' },
    { label: 'Least replied', value: 'RepliedAsc' },
  ],
  [TemplateType.LINKEDIN]: [
    ...baseTemplateSortOptions,
    { label: 'Most replied', value: 'Replied' },
    { label: 'Least replied', value: 'RepliedAsc' },
  ],
  [TemplateType.IN_MAIL]: [
    ...baseTemplateSortOptions,
    { label: 'Most replied', value: 'Replied' },
    { label: 'Least replied', value: 'RepliedAsc' },
  ],
  [TemplateType.SCRIPT]: [
    ...baseTemplateSortOptions,
    { label: 'Most replied', value: 'Replied' },
    { label: 'Least replied', value: 'RepliedAsc' },
  ],
  [TemplateType.VOICE_NOTE]: [
    ...baseTemplateSortOptions,
    { label: 'Most replied', value: 'Replied' },
    { label: 'Least replied', value: 'RepliedAsc' },
  ],
  [TemplateType.INVITATION]: [
    ...baseTemplateSortOptions,
    { label: 'Most accepted', value: 'Replied' },
    { label: 'Least accepted', value: 'RepliedAsc' },
  ],
};

export const getTemplateSortOptions = (
  templateType: TemplateType,
): TemplateSortOption[] =>
  templatesSortingOptions[templateType] ??
  templatesSortingOptions[TemplateType.EMAIL];

export const getTemplateSortValues = (
  templateType: TemplateType,
): TemplateSearchSort[] =>
  getTemplateSortOptions(templateType).map((option) => option.value);

export const renderTemplateSortOptions = (
  templateType: TemplateType,
  sortName: TemplateSearchSort,
): string => {
  const options = getTemplateSortOptions(templateType);
  const byType = options.find((opt) => opt.value === sortName);
  if (byType) return byType.label;

  const fallback = getTemplateSortOptions(TemplateType.EMAIL).find(
    (opt) => opt.value === sortName,
  );

  return fallback?.label ?? '';
};

export const splitEPsToTagsAndNotTags = (
  externalProperties?: ExternalProperty[] | null,
  checkIsEditable = false,
) => {
  if (externalProperties) {
    return externalProperties.reduce(
      (acc, curr) => {
        if (curr.isEditable || !checkIsEditable) {
          if (curr.isTag) {
            acc[0].push(curr);
          } else {
            acc[1].push(curr);
          }
        }
        return acc;
      },
      [[], []] as [ExternalProperty[], ExternalProperty[]],
    );
  }

  return [[], []] as [ExternalProperty[], ExternalProperty[]];
};

export function getDateFromScheduleTime(
  scheduleTime: string | null | undefined,
) {
  if (!scheduleTime) {
    const date = new Date();
    date.setHours(9, 0, 0, 0);
    return date;
  }

  // time is in format HH:mm+TZ
  // split by + or - to get time and timezone
  const parts = scheduleTime.split(/[-+]/);

  const time = parts[0];

  //In format +/-HH:MM
  const timeZone: string | undefined = parts[1];

  const timeZoneSign = scheduleTime.length >= 5 ? scheduleTime[5] : null;
  const timeZoneParts = timeZone?.split(':');
  const timeZoneHours = timeZoneParts ? parseInt(timeZoneParts[0]) : null;
  const timeZoneMinutes = timeZoneParts ? parseInt(timeZoneParts[1]) : null;

  const date = new Date();
  const timeParts = time.split(':');

  date.setUTCHours(
    parseInt(timeParts[0]) +
      (!!timeZoneSign && timeZoneParts[0] && timeZoneHours
        ? timeZoneSign === '+'
          ? -timeZoneHours
          : timeZoneHours
        : 0),
  );
  date.setUTCMinutes(
    parseInt(timeParts[1]) +
      (!!timeZoneSign && timeZoneParts[1] && timeZoneMinutes
        ? timeZoneSign === '+'
          ? -timeZoneMinutes
          : timeZoneMinutes
        : 0),
  );
  return date;
}

export const sortStagesByDisplayOrder = (stages: Stage[]) => {
  //I order them by stageOrder
  const entries = Object.entries(stages);
  entries.sort((e1, e2) => {
    const a = e1[1];
    const b = e2[1];
    if (a.isClosed && b.isClosed) {
      if (a.isWon && !b.isWon) {
        return -1;
      }
      if (!a.isWon && b.isWon) {
        return 1;
      }
      return 0;
    }
    if (a.isClosed) {
      return 1;
    }
    if (b.isClosed) {
      return -1;
    }
    return a.displayOrder - b.displayOrder;
  });
  //Remove the name, since it is already contained as property
  return entries.map((entry) => entry[1]);
};

export const parseDateValueFromMultipleFormats = (
  value?: string | number | Date | null,
  dateOnly = false,
): Date | null => {
  if (!value) return null;

  let date: Date;

  // Numeric value (seconds or milliseconds)
  if (typeof value === 'number') {
    date = new Date(value < 1e12 ? value * 1000 : value);
  }
  // Numeric-like string (e.g., "1698748800" or "1698748800000")
  else if (!isNaN(Number(value))) {
    const num = Number(value);
    date = new Date(num < 1e12 ? num * 1000 : num);
  }
  // Date string (e.g., "2023-10-05T14:48:00.000Z" or "2023-10-05")
  else {
    date = new Date(value);
  }

  if (isNaN(date.getTime())) return null;

  if (dateOnly) {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  }

  return date;
};

export function checkIfLeadsEnabled(user: User | null | undefined) {
  return (
    doesCrmSupportLeads(user?.company?.integrationType) &&
    user?.otherSettings?.hasLeads
  );
}

/** Whether a new prospect starts as a Lead, following the subscription's default creation type. */
export function getDefaultCreateAsLead(user: User | null | undefined) {
  return (
    !!checkIfLeadsEnabled(user) &&
    user?.otherSettings?.defaultCreationType !== 'Prospect'
  );
}

/**
 * Whether a prospect has to be created as a Lead because its company won't be
 * in the CRM: it is in Crono but not synced, or it is new and created only in
 * Crono.
 * @param account the Crono company the prospect is linked to; none for a new company.
 * @param createInCrm whether the prospect, and a new company with it, is created in the CRM.
 */
export function mustCreateAsLead(
  user: User | null | undefined,
  account: { isSynced: boolean } | null | undefined,
  createInCrm: boolean,
) {
  if (!checkIfLeadsEnabled(user)) return false;
  if (account) return !account.isSynced;
  return !createInCrm;
}

export function checkIsProspectLead(
  user: User | null | undefined,
  prospect:
    | Prospect
    | ProspectSearchStrategyResponse
    | ProspectLinkedin
    | null
    | undefined,
) {
  if (!user || !prospect) {
    return false;
  }

  return checkIfLeadsEnabled(user) && !prospect?.fromContact;
}
